import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { buildApiUrl } from "../config/apiConfig";
import { loginRequest } from "../config/msalConfig";
import FYSelectionModal from "./FYSelectionModal";
import AuditTypeModal from "./AuditTypeModal";
import ControlCategoryModal from "./ControlCategoryModal";
import ApplicationSelectionModal from "./ApplicationSelectionModal";
import { setControlCategory as persistControlCategory } from "../utils/financialScope";
import "../styles/Login.css";
import thermaxLogo from "../assets/Thermax-Logo.wine01.png";

function Login() {
  console.log("Login component loaded");
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const successMessage = location.state?.message || "";

  // location.state persists on the browser history entry - without clearing
  // it, navigating back to /login (or a refresh that reuses this entry)
  // would keep re-showing the same success message indefinitely.
  useEffect(() => {
    if (successMessage) {
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [showFYModal, setShowFYModal] = useState(false);
  const [showAuditTypeModal, setShowAuditTypeModal] = useState(false);
  const [showControlCategoryModal, setShowControlCategoryModal] = useState(false);
  const [controlCategoryAccess, setControlCategoryAccess] = useState(null);
  const [pendingRedirect, setPendingRedirect] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const [showAppSelectModal, setShowAppSelectModal] = useState(false);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [appSelectLoading, setAppSelectLoading] = useState(false);
  const [appSelectError, setAppSelectError] = useState("");

  // Stores the login response (token/user) and kicks off the ITGC/ITAC ->
  // application-selection (first login only) -> FY -> audit-type modal
  // flow before redirecting.
  const completeLogin = async (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));

    if (data.must_change_password) {
      navigate('/set-password');
      return;
    }

    const redirectPath = data.user.is_superuser
      ? "/superadmin-dashboard"
      : (data.user.is_staff ? "/admin-dashboard" : "/landing");
    setPendingRedirect(redirectPath);
    setPendingUser(data.user);

    try {
      const res = await fetch(buildApiUrl("control-category-access/"), {
        headers: { Authorization: `Token ${data.token}` },
      });
      const accessData = await res.json().catch(() => ({}));
      setControlCategoryAccess(res.ok ? accessData : null);
    } catch (err) {
      console.error("Failed to load control category access:", err);
      setControlCategoryAccess(null);
    }

    setShowControlCategoryModal(true);
  };

  // Called once the user picks ITGC/ITAC. Application assignments are made
  // per control_category, so the first-login "which application do you
  // belong to" prompt is (re-)evaluated here, scoped to the chosen category,
  // rather than off the pre-category-selection login response.
  const handleControlCategorySelect = async (category) => {
    persistControlCategory(category);
    setShowControlCategoryModal(false);

    const user = pendingUser;
    const token = localStorage.getItem('token');
    const needsApplicationSelection = user && !user.is_staff && !user.is_superuser && !user.is_application_head;

    if (needsApplicationSelection) {
      try {
        const res = await fetch(buildApiUrl("my-application-assignments/"), {
          headers: { Authorization: `Token ${token}`, "X-Control-Category": category },
        });
        const assignData = await res.json().catch(() => ({}));
        const myApps = assignData.applications || [];
        if (myApps.length === 0) {
          const listRes = await fetch(buildApiUrl("applications-list/"), {
            headers: { Authorization: `Token ${token}` },
          });
          const listData = await listRes.json().catch(() => ({}));
          setAvailableApplications(listData.applications || []);
          setShowAppSelectModal(true);
          return;
        }
        localStorage.setItem('applications', JSON.stringify(myApps));
      } catch (err) {
        console.error("Failed to load application assignments:", err);
      }
    }

    setShowFYModal(true);
  };

  // Called once the user picks an application in ApplicationSelectionModal.
  // Persists the assignment server-side, stores the resulting applications
  // list, then continues into the FY/audit-type flow as normal.
  const handleApplicationSelect = async (applicationId) => {
    setAppSelectError("");
    setAppSelectLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(buildApiUrl("select-application/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
          "X-Control-Category": localStorage.getItem('control_category') || 'ITGC',
        },
        body: JSON.stringify({ application_id: applicationId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Could not save your application selection.");
      }
      localStorage.setItem('applications', JSON.stringify(data.applications));
      setShowAppSelectModal(false);
      setShowFYModal(true);
    } catch (err) {
      setAppSelectError(err.message || "Could not save your application selection.");
    } finally {
      setAppSelectLoading(false);
    }
  };

  // After the Microsoft redirect lands back on this page, MSAL resolves the
  // pending auth response here with the id_token, which we hand to the
  // backend for verification (see Control/sso.py + /api/sso-login/).
  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then(async (response) => {
        if (!response || !response.idToken) {
          return;
        }
        setSsoLoading(true);
        const apiResponse = await fetch(buildApiUrl("sso-login/"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id_token: response.idToken }),
        });
        const data = await apiResponse.json().catch(() => ({}));
        if (apiResponse.status !== 200) {
          throw new Error(data.error || `Microsoft sign-in failed with status ${apiResponse.status}`);
        }
        completeLogin(data);
      })
      .catch((err) => {
        console.error("Microsoft sign-in error:", err);
        setError(err.message || "Microsoft sign-in failed. Please try again.");
      })
      .finally(() => setSsoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMicrosoftSignIn = () => {
    setError("");
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error("Microsoft sign-in redirect error:", err);
      setError(err.message || "Could not start Microsoft sign-in.");
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const apiUrl = buildApiUrl("login/");

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      if (response.status === 200) {
        const data = await response.json();
        completeLogin(data);
      } else {
        const errorData = await response.json().catch(() => ({ error: "Unable to parse error response" }));
        throw new Error(errorData.error || errorData.message || `Login failed with status ${response.status}`);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Failed to connect to server. Make sure Django is running on port 8000");
    } finally {
      setLoading(false);
    }
  };

  const handleFYSelect = (fy) => {
    console.log("Financial year selected:", fy);
    setShowFYModal(false);
    setShowAuditTypeModal(true);
  };

  const handleAuditTypeSelect = (auditType) => {
    console.log("Audit type selected:", auditType);
    localStorage.setItem('audit_type', auditType);
    setShowAuditTypeModal(false);
    navigate(pendingRedirect);
  };

  return (
    <div className="login-page">
      <img src={thermaxLogo} alt="Thermax" className="login-logo" />
      {(showFYModal || showAuditTypeModal || showAppSelectModal || showControlCategoryModal) && <div className="login-bg-overlay" />}
      {showControlCategoryModal && <ControlCategoryModal onSelect={handleControlCategorySelect} allowedCategories={controlCategoryAccess} />}
      {showAppSelectModal && (
        <ApplicationSelectionModal
          applications={availableApplications}
          onSelect={handleApplicationSelect}
          loading={appSelectLoading}
          error={appSelectError}
        />
      )}
      {showFYModal && <FYSelectionModal onSelect={handleFYSelect} />}
      {showAuditTypeModal && <AuditTypeModal onSelect={handleAuditTypeSelect} />}
      <div className="landing-bg-credit" aria-hidden="true">
        © Powered by Digital Team and PMO Team
      </div>
      <div className="login-container">
        <div className="login-body">
          <div className="login-form-col">
            <h1 className="login-title">Welcome to Parivekshan</h1>
            <p className="login-subtitle">Sign in to continue.</p>
            {successMessage && (
              <div className="login-error" style={{ background: "rgba(34, 197, 94, 0.15)", borderColor: "#22c55e", color: "#dcfce7" }}>
                {successMessage}
              </div>
            )}
            {error && <div className="login-error">{error}</div>}
            <button
              type="button"
              className="login-btn sso-login-btn"
              onClick={handleMicrosoftSignIn}
              disabled={ssoLoading}
            >
              <svg className="ms-logo" width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              {ssoLoading ? 'Signing in...' : 'Sign in with SSO'}
            </button>
            <div className="login-divider">
              <span className="login-divider-line" />
              <span className="login-divider-text">or external users</span>
              <span className="login-divider-line" />
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="password">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <p className="forgot-password" onClick={() => navigate('/forgot-password')}>Forgot Password?</p>
              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Logging in...' : 'Log In'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
